import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import CheckBox from 'commons/src/components/input/Checkbox';
import Input from 'commons/src/components/input/Input';
import InputInfo from 'commons/src/components/input/InputInfo';
import RadioButtons from 'commons/src/components/input/Radio';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { sensorUnits as sensorUnitLabels } from 'commons/src/constants';
import { LocationType, Units } from 'commons/src/models/commonTypeScript';
import { SelectionModal, SelectionModalItem } from '../../../components/SelectionModal';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import FileUploadField from './FileUploadField';
import styles from './mqttPublisher.module.scss';
import {
    DEFAULT_MQTT_PORT,
    DEFAULT_MQTTS_PORT,
    MQTT_EVENT_TYPES,
    MqttPublisherAuthType,
    MqttPublisherFormValues,
    MqttPublisherProtocol,
} from './mqttPublisherTypes';

type Props = {
    createConfig: (values: MqttPublisherFormValues) => Promise<{ clientId: string }>;
    createLoading: boolean;
};

const MqttPublisherCreateForm = ({ createConfig, createLoading }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const userUnits = useSelector((state: Store) => state.userSettings.units);
    const locations: LocationType[] = useSelector((state: Store) => state.locations.locations);

    const [name, setName] = useState('');
    const [protocol, setProtocol] = useState<MqttPublisherProtocol>('MQTT');
    const [host, setHost] = useState('');
    const [port, setPort] = useState(DEFAULT_MQTT_PORT);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [clientCert, setClientCert] = useState<File | undefined>();
    const [clientKey, setClientKey] = useState<File | undefined>();
    const [caCert, setCaCert] = useState<File | undefined>();
    const [eventTypes, setEventTypes] = useState<string[]>(MQTT_EVENT_TYPES.map(e => e.value));
    const [displayValidation, setDisplayValidation] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [showBuildingsModal, setShowBuildingsModal] = useState(false);

    const [sensorUnits, setSensorUnits] = useState<Units>({
        radonUnit: userUnits.radonUnit || 'bq',
        tempUnit: userUnits.tempUnit || 'c',
        pressureUnit: userUnits.pressureUnit || 'hpa',
        vocUnit: userUnits.vocUnit || 'ppb',
    });

    useEffect(() => {
        setPort(protocol === 'MQTTS' ? DEFAULT_MQTTS_PORT : DEFAULT_MQTT_PORT);
        if (protocol === 'MQTT') {
            setClientCert(undefined);
            setClientKey(undefined);
            setCaCert(undefined);
        }
    }, [protocol]);

    const buildingItems: SelectionModalItem[] = locations.map(loc => ({ id: loc.id, label: loc.name }));
    const allLocationIds = locations.map(loc => loc.id);
    const isSubscribedToAll = selectedLocations.length === 0;
    const effectiveSelectedIds = isSubscribedToAll ? allLocationIds : selectedLocations;

    const handleBuildingsSave = (ids: string[]): void => {
        setSelectedLocations(ids.length === locations.length ? [] : ids);
    };

    const selectedLocationNames = locations.filter(loc => selectedLocations.includes(loc.id)).map(loc => loc.name);
    const buildingsText = isSubscribedToAll
        ? txt('MqttPublisher.AllBuildingsSelected')
        : selectedLocationNames.join(', ');

    const isValid = name.length > 0 && host.length > 0 && port >= 1 && port <= 65535 && eventTypes.length > 0;

    const handleCreate = async (): Promise<void> => {
        if (!isValid) {
            setDisplayValidation(true);
            return;
        }
        try {
            const hasCerts = !!(clientCert && clientKey);
            const authType: MqttPublisherAuthType = hasCerts ? 'CERTIFICATE_BASED' : 'BASIC_AUTHENTICATION';
            const config = await createConfig({
                name,
                protocol,
                host,
                port,
                authType,
                username: username || undefined,
                password: password || undefined,
                clientCertificate: clientCert,
                clientKey,
                caCertificate: caCert,
                active: false,
                subscribedToAll: isSubscribedToAll,
                eventTypes,
                locations: selectedLocations,
                sensorUnits,
            });
            navigate(`/${paths.mqttPublisher}/${config.clientId}`);
        } catch {
            // error is handled by the hook
        }
    };

    return (
        <form>
            <div className="page-wrapper__medium page-wrapper__medium--white">
                <div className="settings-details-container settings-details-container--margin">
                    <h3 className={styles.sectionHeader}>{txt('MqttPublisher.General')}</h3>
                    <Input
                        id="publisherName"
                        label="MqttPublisher.Name"
                        type="text"
                        validate={displayValidation}
                        isValid={name.length > 0}
                        hint="MqttPublisher.NameRequired"
                        markedMandatory
                        currentValue={name}
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void => setName(e.currentTarget.value)}
                    />
                    <div className={styles.buildingsSection}>
                        <div className={styles.buildingsSection__label}>{txt('MqttPublisher.Buildings')}</div>
                        <div className={styles.buildingsSection__row}>
                            <span>{buildingsText}</span>
                            <button
                                type="button"
                                className={styles.buildingsSection__editLink}
                                onClick={(): void => setShowBuildingsModal(true)}
                            >
                                <MaterialIcon name="edit" />
                                <span>{txt('MqttPublisher.ViewEditBuildings')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-wrapper__medium page-wrapper__medium--white">
                <div className="settings-details-container settings-details-container--margin">
                    <h3 className={styles.sectionHeader}>{txt('MqttPublisher.Connection')}</h3>
                    <RadioButtons
                        header="MqttPublisher.Protocol"
                        buttons={[
                            { value: 'MQTT', label: 'MQTT' },
                            { value: 'MQTTS', label: 'MQTTs' },
                        ]}
                        selectorName="protocol"
                        row
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void =>
                            setProtocol(e.currentTarget.value as MqttPublisherProtocol)
                        }
                        value={protocol}
                        labelId="protocol"
                    />
                    <div className={styles.hostPortRow}>
                        <Input
                            id="publisherHost"
                            label="MqttPublisher.HostIp"
                            type="text"
                            validate={displayValidation}
                            isValid={host.length > 0}
                            hint="MqttPublisher.HostRequired"
                            markedMandatory
                            currentValue={host}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void => setHost(e.currentTarget.value)}
                        />
                        <Input
                            id="publisherPort"
                            label="MqttPublisher.Port"
                            type="number"
                            validate={displayValidation}
                            isValid={port >= 1 && port <= 65535}
                            hint="MqttPublisher.PortRequired"
                            markedMandatory
                            currentValue={String(port)}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void =>
                                setPort(Number(e.currentTarget.value))
                            }
                        />
                    </div>
                    <Input
                        id="publisherUsername"
                        label="MqttPublisher.Username"
                        type="text"
                        validate={false}
                        currentValue={username}
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void => setUsername(e.currentTarget.value)}
                    />
                    <Input
                        id="publisherPassword"
                        label="MqttPublisher.Password"
                        type="password"
                        validate={false}
                        currentValue={password}
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void => setPassword(e.currentTarget.value)}
                    />
                    {protocol === 'MQTTS' && (
                        <>
                            <FileUploadField
                                label="MqttPublisher.ClientCertificate"
                                file={clientCert}
                                onChange={setClientCert}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                            />
                            <FileUploadField
                                label="MqttPublisher.ClientKey"
                                file={clientKey}
                                onChange={setClientKey}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                            />
                            <FileUploadField
                                label="MqttPublisher.CaCertificate"
                                file={caCert}
                                onChange={setCaCert}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="page-wrapper__medium page-wrapper__medium--white">
                <div className="settings-details-container settings-details-container--margin">
                    <div className={styles.eventTypesSection}>
                        <div className={styles.eventTypesSection__header}>
                            {txt('MqttPublisher.EventTypes')}
                            <InputInfo infoText="MqttPublisher.EventTypesInfo" />
                        </div>
                        {MQTT_EVENT_TYPES.map(({ value, label, info }) => (
                            <CheckBox
                                key={value}
                                id={`event-type-${value}`}
                                label={
                                    <span className={styles.eventTypeLabel}>
                                        {txt(label)}
                                        <InputInfo infoText={info} />
                                    </span>
                                }
                                labelIsElement
                                checked={eventTypes.includes(value)}
                                onChange={(): void =>
                                    setEventTypes(prev =>
                                        prev.includes(value) ? prev.filter(e => e !== value) : [...prev, value]
                                    )
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="page-wrapper__medium page-wrapper__medium--white">
                <div className="settings-details-container settings-details-container--margin">
                    <div className={styles.unitsHeader}>
                        <h3 className={styles.sectionHeader}>{txt('MqttPublisher.Units')}</h3>
                        <span className={styles.unitsHint}>{txt('MqttPublisher.UnitsPreFilled')}</span>
                    </div>
                    {(
                        [
                            {
                                label: 'Radon',
                                key: 'radonUnit' as const,
                                options: [
                                    { value: 'bq', label: sensorUnitLabels.bq },
                                    { value: 'pci', label: sensorUnitLabels.pci },
                                ],
                            },
                            {
                                label: 'Temperature',
                                key: 'tempUnit' as const,
                                options: [
                                    { value: 'c', label: txt('Units.UnitCelsius') },
                                    { value: 'f', label: txt('Units.UnitFahrenheit') },
                                ],
                            },
                            {
                                label: 'Pressure',
                                key: 'pressureUnit' as const,
                                options: [
                                    { value: 'hpa', label: sensorUnitLabels.hpa },
                                    { value: 'inhg', label: sensorUnitLabels.inhg },
                                ],
                            },
                            {
                                label: 'VOC',
                                key: 'vocUnit' as const,
                                options: [
                                    { value: 'ppb', label: sensorUnitLabels.ppb },
                                    { value: 'mgpc', label: sensorUnitLabels.mgpc },
                                ],
                            },
                        ] as const
                    ).map(({ label, key, options }) => (
                        <div key={key} className={styles.unitRow}>
                            <span className={styles.unitRow__label}>{txt(label)}</span>
                            <div className={styles.unitToggle}>
                                {options.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`${styles.unitToggle__option} ${
                                            sensorUnits[key] === option.value
                                                ? styles['unitToggle__option--active']
                                                : ''
                                        }`}
                                        onClick={(): void => setSensorUnits({ ...sensorUnits, [key]: option.value })}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="page-wrapper__medium">
                <div className={styles.createButtonRow}>
                    <PrimaryButton
                        onClick={handleCreate}
                        type="button"
                        title="MqttPublisher.Create"
                        loading={createLoading}
                        color="primary"
                        testAttr="create-mqtt-publisher"
                    />
                </div>
            </div>

            <SelectionModal
                isOpen={showBuildingsModal}
                onClose={(): void => setShowBuildingsModal(false)}
                items={buildingItems}
                selectedIds={effectiveSelectedIds}
                onSave={handleBuildingsSave}
                searchPosition="top"
                showColumnActions
                compactButtons
                title={txt('MqttPublisher.SelectBuildings')}
                availableTitle={txt('MqttPublisher.AllBuildings')}
                selectedTitle={txt('MqttPublisher.SelectedBuildings', { count: 0 }).replace(/\s*\(\d+\)/, '')}
                emptyMessage={txt('MqttPublisher.NoBuildings')}
                allSelectedMessage={txt('MqttPublisher.AllBuildingsSelected')}
                noSelectionMessage={txt('MqttPublisher.NoBuildingsSelected')}
            />
        </form>
    );
};

export default MqttPublisherCreateForm;
